import { Box, FormControlLabel, FormHelperText, Radio } from "@mui/material";
import React, { useState } from "react";

import { TextInput } from "components/common/TextInput";
import { useIsDetectorNameValid } from "components/createDetector/hooks";
import { Modal } from "new-components/modals/Modal";
import { Typography } from "new-components/typography/Typography";

// TODO: can the object values from OptionInformation be incorporated into the enum?
export enum ActionOptions {
  Rename = "Rename",
  Edit = "Edit",
  // Duplicate = "Duplicate",
  Reset = "Reset",
  Delete = "Delete",
}

export type OptionInformationDict = {
  [key: string]: { menuText: string; subtext: string; title: string; buttonText: string; contentText?: string };
};

export const OptionInformation: OptionInformationDict = {
  [ActionOptions.Rename]: { menuText: "Rename", subtext: "", title: "Rename Detector", buttonText: "Rename" },
  [ActionOptions.Edit]: {
    menuText: "Edit Query",
    subtext: "",
    title: "Save Changes: Edit Query Options",
    buttonText: "Save",
    contentText: "How would you like to proceed with Detector Data associated with edited Query Text?",
  },
  // [ActionOptions.Duplicate]: {
  //   menuText: "Duplicate",
  //   subtext: "(save as new)",
  //   title: "Duplicate Detector",
  //   buttonText: "Duplicate",
  // },
  [ActionOptions.Reset]: {
    menuText: "Reset Detector",
    subtext: "(clear all training)",
    title: "Reset Detector?",
    buttonText: "Reset",
    contentText: "Reset will remove all cloud labels from the existing detector.",
  },
  [ActionOptions.Delete]: { menuText: "Delete Detector", subtext: "", title: "Delete Detector?", buttonText: "Delete" },
};

interface DetectorActionModalProps {
  type: ActionOptions;
  dialogIsOpen: boolean;
  setDialogIsOpen: (value: boolean) => void;
  handleMenuClose: () => void;
  detectorName: string;
  detectorGroupId: string;
  setConfirmText: (value: string) => void;
  setConfirmIsOpen: (value: boolean) => void;
  queryText: string;
  doPatch: (field: string, value: string) => void;
  doDelete: () => void;
  doReset: () => void;
}

export const DetectorActionModal = React.memo((props: DetectorActionModalProps) => {
  const {
    type,
    dialogIsOpen,
    setDialogIsOpen,
    handleMenuClose,
    detectorName,
    detectorGroupId,
    setConfirmText,
    setConfirmIsOpen,
    queryText,
    doPatch,
    doDelete,
    doReset,
  } = props;

  const [retainDataEdit, setRetainDataEdit] = useState(true);
  const [newDetectorName, setNewDetectorName] = useState(detectorName);
  const { error: detectorNameError, isValidating: isValidatingDetectorName } = useIsDetectorNameValid(
    newDetectorName,
    detectorGroupId,
    type === ActionOptions.Rename && detectorName !== newDetectorName
  );

  const handleCloseDialog = () => setDialogIsOpen(false);

  const getTitleText = () => OptionInformation[type].title;

  function getContent() {
    switch (type) {
      case ActionOptions.Rename:
        return (
          <Box sx={{ width: "100%" }}>
            <TextInput
              value={newDetectorName}
              data-testid="detector-name"
              onChange={(event) => setNewDetectorName(event.target.value)}
              InputProps={{
                className: "text-xl bg-white",
              }}
              fullWidth
              error={detectorNameError !== ""}
            />

            {detectorNameError && (
              <FormHelperText error className="truncate m-2 mb-0">
                {detectorNameError}
              </FormHelperText>
            )}
          </Box>
        );
      case ActionOptions.Edit:
        return (
          <Box className="flex flex-col gap-4">
            <Typography variant="subtitle" align="center">
              {OptionInformation[type].contentText}
            </Typography>
            <Box className="flex flex-col gap-2 items-center">
              <FormControlLabel
                control={
                  <Radio checked={retainDataEdit} onChange={(event) => setRetainDataEdit(event.target.checked)} />
                }
                label={<Typography className="ml-[9px]">Retain Training Data, Examples, &amp; Stats</Typography>}
              />
            </Box>
            <Box className="flex flex-col gap-2 items-center">
              <FormControlLabel
                control={
                  <Radio checked={!retainDataEdit} onChange={(event) => setRetainDataEdit(!event.target.checked)} />
                }
                label={<Typography className="ml-[12px]">Discard Training Data, Examples, &amp; Stats</Typography>}
              />
            </Box>
          </Box>
        );
      case ActionOptions.Reset:
      case ActionOptions.Delete: {
        const mainContent = OptionInformation[type].contentText ? (
          <Typography variant="subtitle" align="center" className="mb-2">
            {OptionInformation[type].contentText}
          </Typography>
        ) : undefined;
        const permanentWarning = (
          <Typography variant="subtitle" align="center">
            This is a permanent action and cannot be undone.
          </Typography>
        );

        return (
          <Box className="flex flex-col items-center">
            {mainContent}
            {permanentWarning}
          </Box>
        );
      }
      default:
        return <Typography>Default</Typography>;
    }
  }

  const handleSubmit = () => {
    let confirmText;
    switch (type) {
      case ActionOptions.Rename:
        confirmText = "Detector Renamed";
        doPatch("name", newDetectorName);
        break;
      case ActionOptions.Edit:
        confirmText = `${detectorName} Changes Saved`;
        doPatch("label_prompt", queryText);
        if (!retainDataEdit) {
          doReset();
        }
        break;
      case ActionOptions.Reset:
        confirmText = `${detectorName} Reset`;
        doReset();
        break;
      case ActionOptions.Delete:
        confirmText = `${detectorName} Deleted`;
        doDelete();
        break;
      default:
        confirmText = "Success";
    }
    setConfirmText(confirmText);
    handleMenuClose();
    handleCloseDialog();
    setConfirmIsOpen(true);
  };

  function isValidForm() {
    switch (type) {
      case ActionOptions.Rename:
        return (
          newDetectorName !== detectorName && newDetectorName.length && !detectorNameError && !isValidatingDetectorName
        );
      default:
        return true;
    }
  }

  return (
    <Modal
      open={dialogIsOpen}
      onClose={handleCloseDialog}
      size="small"
      title={getTitleText()}
      body={getContent()}
      onCancel={handleCloseDialog}
      onConfirmation={handleSubmit}
      isConfirmationDisabled={!isValidForm()}
      confirmationButtonTitle={OptionInformation[type].buttonText}
    />
  );
});
